/* Custom mobile navigation drawer (< 990px).
   - Slides in from the left and pushes the rest of the page to the right.
   - Moves itself to <body> on init so the page-push transform never affects it.
   - Handles the mega sub-menu sliding (in from the right) with a Back button.
   No global scope pollution; all listeners cleaned up on disconnect.

   NOTE on the portal: appendChild(this) synchronously re-fires
   disconnectedCallback + connectedCallback. Setup must therefore only run on
   the second connect (guarded by `initialized`), otherwise every listener is
   bound twice and a tap opens + instantly closes the drawer. */
if (!customElements.get('cbg-mobile-nav')) {
  customElements.define(
    'cbg-mobile-nav',
    class CbgMobileNav extends HTMLElement {
      connectedCallback() {
        // Portal to <body> so `body > *` page-push transforms don't move the
        // drawer. Setup happens in the connectedCallback of the re-insertion.
        if (this.parentElement !== document.body) {
          // Drop stale copies (theme editor re-renders the header section).
          document.querySelectorAll('body > cbg-mobile-nav').forEach((el) => el.remove());
          document.body.appendChild(this);
          return;
        }

        if (this.initialized) return;
        this.initialized = true;

        // Enables the push transition on sibling elements once JS is ready.
        document.body.classList.add('cbg-mnav-ready');

        this.root = document.documentElement;
        this.burger = document.querySelector('.cbg-burger-btn');
        this.burgerGhost = null; // invisible stand-in that holds the toggle's layout slot
        this.catcher = this.querySelector('.cbg-mnav-catcher');
        this.closeEls = this.querySelectorAll('[data-cbg-mnav-close]');
        this.submenus = this.querySelectorAll('.cbg-mnav__submenu');
        this.openers = this.querySelectorAll('[data-cbg-submenu-open]');
        this.backEls = this.querySelectorAll('[data-cbg-submenu-back]');
        this.toggles = document.querySelectorAll('[data-cbg-mnav-toggle]');
        this.currencyToggles = this.querySelectorAll('[data-cbg-currency-toggle]');
        this.currencyOptions = this.querySelectorAll('[data-cbg-currency-option]');

        this.close = this.close.bind(this);
        this.onToggle = this.onToggle.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onOpenSubmenu = this.onOpenSubmenu.bind(this);
        this.onBack = this.resetSubmenus.bind(this);
        this.onCurrencyToggle = this.onCurrencyToggle.bind(this);
        this.onCurrencyPick = this.onCurrencyPick.bind(this);

        this.toggles.forEach((el) => el.addEventListener('click', this.onToggle));
        this.closeEls.forEach((el) => el.addEventListener('click', this.close));
        this.openers.forEach((el) => el.addEventListener('click', this.onOpenSubmenu));
        this.backEls.forEach((el) => el.addEventListener('click', this.onBack));
        this.currencyToggles.forEach((el) => el.addEventListener('click', this.onCurrencyToggle));
        this.currencyOptions.forEach((el) => el.addEventListener('click', this.onCurrencyPick));
        document.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        if (!this.initialized) return;
        this.initialized = false;

        this.toggles.forEach((el) => el.removeEventListener('click', this.onToggle));
        this.closeEls.forEach((el) => el.removeEventListener('click', this.close));
        this.openers.forEach((el) => el.removeEventListener('click', this.onOpenSubmenu));
        this.backEls.forEach((el) => el.removeEventListener('click', this.onBack));
        this.currencyToggles.forEach((el) => el.removeEventListener('click', this.onCurrencyToggle));
        this.currencyOptions.forEach((el) => el.removeEventListener('click', this.onCurrencyPick));
        document.removeEventListener('keydown', this.onKeydown);

        // Never leave the page locked/pushed (or the toggle stranded in the
        // removed drawer) if the drawer is removed mid-open.
        this.returnToggle();
        this.root.classList.remove('cbg-mnav-open');
        this.root.classList.remove('cbg-mnav-closing');
      }

      onToggle(event) {
        event.preventDefault();
        if (this.root.classList.contains('cbg-mnav-open')) {
          this.close();
        } else {
          this.open();
        }
      }

      open() {
        this.adoptToggle();
        this.root.classList.add('cbg-mnav-open');
        this.setAttribute('open', '');
        this.toggles.forEach((el) => el.setAttribute('aria-expanded', 'true'));
      }

      close() {
        this.root.classList.remove('cbg-mnav-open');
        // Keeps overflow-x hidden on <body> for the 0.3s slide-back, so the
        // still-translated content can't produce a horizontal scrollbar.
        this.root.classList.add('cbg-mnav-closing');
        this.removeAttribute('open');
        this.toggles.forEach((el) => el.setAttribute('aria-expanded', 'false'));
        // After the close animation: unlock overflow-x, return the toggle to
        // its header slot, reset sub-menus and collapse the currency dropdown.
        window.setTimeout(() => {
          // Bail if the drawer was re-opened during the slide-back.
          if (this.root.classList.contains('cbg-mnav-open')) return;
          this.root.classList.remove('cbg-mnav-closing');
          this.returnToggle();
          this.resetSubmenus();
          this.closeCurrencyDropdowns();
        }, 300);
      }

      // --- Single-toggle adoption -------------------------------------------
      // The header burger is the ONE toggle. While open it must sit above the
      // dim plane (the drawer's shadow, z-9999), but the sticky header is its
      // own stacking context — no descendant of it can rise above that. So on
      // open we move the SAME button in here (cbg-mobile-nav, z-10000 rules),
      // pinned (position: fixed, CSS) at the exact spot it occupied in the
      // header, leaving a ghost behind to hold its layout slot; CSS then
      // slides it right by the push distance and morphs its icon to the X.
      // On close it slides back and swaps places with the ghost again.

      adoptToggle() {
        const btn = this.burger;
        if (!btn || btn.parentElement === this) return;
        const rect = btn.getBoundingClientRect();
        // An invisible ghost takes the button's place in the header grid, so
        // its column keeps its width and the logo/icons never reflow or jump
        // while the real button is away. Same classes + inline size vars =
        // identical layout footprint; visibility:hidden = zero paint.
        const ghost = document.createElement('span');
        ghost.className = 'cbg-burger-btn cbg-burger-btn--ghost';
        ghost.style.cssText = btn.getAttribute('style') || '';
        ghost.setAttribute('aria-hidden', 'true');
        btn.parentElement.insertBefore(ghost, btn);
        this.burgerGhost = ghost;
        btn.style.top = `${rect.top}px`;
        btn.style.left = `${rect.left}px`;
        this.appendChild(btn);
        // Commit the pinned start position before the open class lands, so the
        // slide transitions from here instead of jumping.
        btn.getBoundingClientRect();
      }

      returnToggle() {
        const btn = this.burger;
        if (!btn || !this.burgerGhost || btn.parentElement !== this) return;
        btn.style.top = '';
        btn.style.left = '';
        if (this.burgerGhost.isConnected) {
          this.burgerGhost.replaceWith(btn);
        } else {
          // Header re-rendered while open (theme editor): a fresh burger
          // exists there already — drop the stale one instead of duplicating.
          btn.remove();
        }
        this.burgerGhost = null;
      }

      onOpenSubmenu(event) {
        event.preventDefault();
        const handle = event.currentTarget.getAttribute('data-cbg-submenu-open');
        const target = this.querySelector(`.cbg-mnav__submenu[data-submenu="${handle}"]`);
        if (target) target.classList.add('is-active');
      }

      resetSubmenus() {
        this.submenus.forEach((el) => el.classList.remove('is-active'));
      }

      // --- Currency dropdown (compact custom select in the drawer footer) ---

      onCurrencyToggle(event) {
        event.preventDefault();
        const button = event.currentTarget;
        const list = document.getElementById(button.getAttribute('aria-controls'));
        if (!list) return;
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        list.hidden = expanded;
      }

      onCurrencyPick(event) {
        event.preventDefault();
        const option = event.currentTarget;
        const form = option.closest('form');
        if (!form) return;
        const input = form.querySelector('input[name="country_code"]');
        if (!input) return;
        input.value = option.getAttribute('data-value');
        form.submit(); // POSTs the localization form → page reloads in the new currency
      }

      closeCurrencyDropdowns() {
        this.currencyToggles.forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
          const list = document.getElementById(button.getAttribute('aria-controls'));
          if (list) list.hidden = true;
        });
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.root.classList.contains('cbg-mnav-open')) {
          this.close();
        }
      }
    }
  );
}
